// Full per-player statistics for an external match (same stat set as panel matches), per map or all maps.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { card, sectionLabel, pill, RGB, ratingOf, RatingLabel } from './extUi';

export const COLS = [
  { key: 'kills', label: 'K', title: 'Kills' },
  { key: 'deaths', label: 'D', title: 'Deaths' },
  { key: 'assists', label: 'A', title: 'Assists' },
  { key: 'kd_diff', label: '+/-', title: 'Kill-death difference', fmt: v => (v > 0 ? `+${v}` : v), color: v => (v > 0 ? 'var(--accent-green)' : v < 0 ? 'var(--accent-red)' : undefined) },
  { key: 'adr', label: 'ADR', title: 'Average damage per round' },
  { key: 'hs_pct', label: 'HS%', title: 'Headshot %', fmt: v => `${v}%` },
  { key: 'kast_pct', label: 'KAST', title: 'Rounds with a kill, assist, survival or trade', fmt: v => `${v}%` },
  { key: 'entry', label: 'FK-FD', title: 'First kills – first deaths', get: p => `${p.entry_kills}-${p.opening_deaths}` },
  { key: 'trades', label: 'TK', title: 'Trade kills', get: p => p.trade_kills },
  { key: 'mk', label: '2K/3K/4K/5K', title: 'Multi-kill rounds', get: p => `${p.multikills['2k']}/${p.multikills['3k']}/${p.multikills['4k']}/${p.multikills['5k']}` },
  { key: 'clutch', label: '1vX', title: 'Clutches won (1v1–1v5)', get: p => p.clutches.won },
  { key: 'mvps', label: 'MVP', title: 'Round MVPs' },
  { key: 'flash_assists', label: 'FA', title: 'Flash assists' },
  { key: 'enemies_flashed', label: 'EF', title: 'Enemies flashed' },
  { key: 'utility_damage', label: 'UD', title: 'Utility damage' },
  { key: 'bomb', label: 'P/D', title: 'Bomb plants / defuses', get: p => `${p.bomb_plants}/${p.bomb_defuses}` },
  { key: 'rating', label: 'Rating', title: 'HLTV Rating 2.0 (estimate)', fmt: v => Number(v).toFixed(2), color: v => (v >= 1.1 ? 'var(--accent-green)' : v < 0.9 ? 'var(--accent-red)' : undefined) },
];

/** Flux 3.0 + Round Swing (panel matches only; services/swing.js) — after COLS where the rows have them. */
export const FLUX3_COLS = [
  { key: 'flux3_rating', label: 'Flux 3.0', title: 'Flux 3.0 - HLTV 3.0-style rating with Round Swing', fmt: v => Number(v || 0).toFixed(2), color: v => (v >= 1.1 ? 'var(--accent-green)' : v < 0.9 ? 'var(--accent-red)' : undefined) },
  { key: 'swing_pct', label: 'Swing', title: "Round Swing: how much the player changes their team's chance to win the round, on average per round", fmt: v => `${v > 0 ? '+' : ''}${Number(v || 0).toFixed(2)}%`, color: v => (v > 0 ? 'var(--accent-green)' : v < 0 ? 'var(--accent-red)' : undefined) },
];

/** Raw value of a column for a player (rating = HLTV 2.0). */
export const cellValue = (c, p) => (c.key === 'rating' ? ratingOf(p) : c.get ? c.get(p) : p[c.key]);

/** Column header (the rating one reads "HLTV 2.0"). */
export function ColHeader({ c, style, onClick, suffix }) {
  const { t } = useTranslation();
  if (c.key === 'rating') {
    return (
      <th style={{ ...style, cursor: onClick ? 'pointer' : style && style.cursor, userSelect: 'none' }} onClick={onClick}
        title={t('ext.stat.ratingHltv', 'HLTV Rating 2.0 (estimate, same formula on every match)')}>
        <RatingLabel />{suffix}
      </th>
    );
  }
  return <th style={style} title={t(`ext.stat.${c.key}`, c.title)} onClick={onClick}>{c.label}{suffix}</th>;
}

// url: the players endpoint (default = the ext match's); panel matches pass /api/v1/matches/:id/players
// (same body). extraCols: columns after COLS, shown only when a player row has that field.
export default function ExtPlayerStats({ tournamentId, matchId, match, refreshKey, url, extraCols = [] }) {
  const { t } = useTranslation();
  const [mapSel, setMapSel] = useState('all');
  const [data, setData] = useState(null);
  const playersUrl = url || `/api/v1/ext/tournaments/${tournamentId}/matches/${matchId}/players`;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(playersUrl, { params: mapSel === 'all' ? {} : { map_index: mapSel } });
      setData(r.data);
    } catch (_) { /* keep last */ }
  }, [playersUrl, mapSel]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const maps = (match.maps || []).filter(m => m.status === 'live' || m.status === 'finished');
  if (!data) return null;
  const all = [...(data.team1 || []), ...(data.team2 || [])];
  const cols = [...COLS, ...extraCols.filter(c => all.some(p => p[c.key] != null))];
  const th ={ ...sectionLabel, padding: '0.45rem 0.45rem', textAlign: 'right', whiteSpace: 'nowrap', cursor: 'help' };
  const td = { padding: '0.42rem 0.45rem', borderTop: '1px solid var(--border-subtle)', textAlign: 'right', fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontSize: '0.78rem' };

  const table = (teamKey) => (
    <div key={teamKey} style={{ marginBottom: '0.9rem' }}>
      <div style={{ fontWeight: 700, fontSize: '0.86rem', color: 'var(--text-primary)', margin: '0.2rem 0 0.35rem' }}>{match.teams[teamKey].name}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', cursor: 'default' }}>{t('ext.sb.player', 'Player')}</th>
              {cols.map(c => <ColHeader key={c.key} c={c} style={th} />)}
            </tr>
          </thead>
          <tbody>
            {data[teamKey].length === 0 && <tr><td colSpan={cols.length + 1} style={{ ...td, textAlign: 'left', fontFamily: 'inherit' }}>{t('ext.sb.noPlayers', 'No players seen yet.')}</td></tr>}
            {data[teamKey].map(p => (
              <tr key={p.steam_id}>
                <td style={{ ...td, textAlign: 'left', fontFamily: 'inherit', color: 'var(--text-primary)', fontWeight: 600 }}>
                  {p.name}{p.team_kills > 0 && <span style={{ ...pill(RGB.red), marginLeft: '0.4rem' }} title={t('ext.stat.teamKills', 'Team kills')}>TK {p.team_kills}</span>}
                </td>
                {cols.map(c => {
                  const v = cellValue(c, p);
                  const color = c.color ? c.color(v) : undefined;
                  return <td key={c.key} style={{ ...td, color: color || td.color, fontWeight: c.key === 'rating' || c.key === 'kills' ? 700 : 400 }}>{c.fmt ? c.fmt(v) : v}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
        <div style={sectionLabel}>{t('ext.playerStats', 'Player statistics')}</div>
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setMapSel('all')} style={{ ...pill(mapSel === 'all' ? RGB.blue : RGB.gray), cursor: 'pointer' }}>{t('ext.allMaps', 'All maps')}</button>
          {maps.map(m => (
            <button key={m.index} type="button" onClick={() => setMapSel(m.index)} style={{ ...pill(mapSel === m.index ? RGB.blue : RGB.gray), cursor: 'pointer' }}>
              {(m.name || t('ext.mapN', 'Map {{n}}', { n: m.index + 1 })).replace(/^de_/, '')}
            </button>
          ))}
        </div>
      </div>
      {table('team1')}
      {table('team2')}
      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
        {t('ext.statsLegend', 'FK-FD first kills – first deaths · TK trade kills · 1vX clutches won · FA flash assists · EF enemies flashed · UD utility damage · P/D bomb plants / defuses')}
      </div>
    </div>
  );
}
